import { Routes } from '@angular/router';
import { ProjectEmbedPage } from './pages/project/project-embed/project-embed.page';
import { AboutAppPage } from './pages/settings/about-app/about-app.page';
import { ProfilePage } from './pages/settings/profile/profile.page';
import { PermissionsPage } from './pages/settings/permissions/permissions.page';
import { PreferencesPage } from './pages/settings/preferences/preferences.page';
import { LoginPage } from './pages/login/login.page';
import { AddressCreatePage } from './pages/address/create.page';
import { WhitelabelGuard } from './guards/white-label.guard';
import { NotificationsPage } from './pages/settings/notifications/notifications.page';
import { projectResolver } from './services/project-resolver.service';
import { privateAppGuard } from './guards/private-app.guard';
import { notProjectAppGuard } from './guards/not-project-app.guard';

function requireLoginInPrivateApp(routes: Routes): Routes {
  return routes.map((route) =>
    route.redirectTo ||
    ['login', 'no-access', '404', 'deeplink/wrapper'].includes(route.path)
      ? route
      : {
          ...route,
          canActivate: [privateAppGuard, ...(route.canActivate ?? [])],
        }
  );
}

export const ROUTES: Routes = requireLoginInPrivateApp([
  {
    path: '',
    loadChildren: () =>
      import('./pages/tabs/tabs.routes').then((m) => m.TABS_ROUTES),
  },
  {
    path: 'projects/:slug',
    loadChildren: () =>
      import('./pages/project/project.routes').then((m) => m.PROJECT_ROUTES),
    canActivate: [WhitelabelGuard],
    resolve: {
      project: projectResolver,
    },
  },
  {
    path: 'survey/:id',
    data: { title: 'project.tab.participate.survey' },
    loadComponent: () =>
      import('./pages/project/project-survey/project-survey.page').then(
        (m) => m.ProjectSurveyPage
      ),
  },
  {
    path: 'participation',
    loadChildren: () =>
      import('./pages/participation/participation.routes').then(
        (m) => m.PARTICIPATION_ROUTES
      ),
  },
  {
    path: 'organisations/:slug',
    data: { title: 'page.organisation' },
    loadComponent: () =>
      import('./pages/organisation/organisation.page').then(
        (m) => m.OrganisationPage
      ),
  },
  {
    path: 'organisations/embed/:slug',
    loadComponent: () =>
      import('./pages/organisation/organisation-embed/organisation-embed.page').then(
        (m) => m.OrganisationEmbedPage
      ),
  },
  {
    path: 'organisations/:slug/update/:id',
    data: { title: 'page.organisation.update' },
    loadComponent: () =>
      import('./pages/organisation/organisation-update/organisation-update.page').then(
        (m) => m.OrganisationUpdatePage
      ),
  },
  {
    path: 'organisations/:slug/conversations/:id',
    data: { title: 'project.tab.conversations.detail' },
    loadComponent: () =>
      import('./pages/conversation/conversation.page').then(
        (m) => m.ConversationPage
      ),
  },
  {
    path: 'notifications',
    data: { title: 'page.notifications' },
    loadComponent: () =>
      import('./pages/notifications/notifications.page').then(
        (m) => m.NotificationsPage
      ),
  },
  {
    path: 'login',
    data: { title: 'page.login' },
    component: LoginPage,
  },
  {
    path: 'about-app',
    data: { title: 'page.settings.about-app' },
    component: AboutAppPage,
  },
  {
    path: 'messages/:id',
    loadComponent: () =>
      import('./pages/message/message.page').then((m) => m.MessagePage),
  },
  {
    path: 'onboarding',
    loadChildren: () =>
      import('./pages/onboarding/onboarding.routes').then(
        (m) => m.ONBOARDING_ROUTES
      ),
  },
  {
    path: 'search/suggestion',
    canActivate: [notProjectAppGuard],
    loadComponent: () =>
      import('./pages/search/suggestion/suggestion.page').then(
        (m) => m.SuggestionPage
      ),
  },
  {
    path: 'settings/profile',
    data: { title: 'page.settings.profile' },
    component: ProfilePage,
  },
  {
    path: 'settings/permissions',
    data: { title: 'page.settings.permissions' },
    component: PermissionsPage,
  },
  {
    path: 'settings/notifications',
    data: { title: 'page.settings.notifications' },
    component: NotificationsPage,
  },
  {
    path: 'settings/preferences',
    data: { title: 'page.settings.preferences' },
    component: PreferencesPage,
  },
  {
    path: 'following/address/create',
    data: { title: 'page.settings.addresses' },
    canActivate: [notProjectAppGuard],
    component: AddressCreatePage,
  },
  {
    path: 'following/address/:id/edit',
    data: { title: 'page.settings.addresses' },
    canActivate: [notProjectAppGuard],
    component: AddressCreatePage,
  },
  {
    path: 'deeplink/wrapper',
    loadComponent: () =>
      import('./pages/deeplink-wrapper/deeplink-wrapper.page').then(
        (m) => m.DeeplinkWrapperPage
      ),
  },
  {
    path: 'embed/:slug',
    component: ProjectEmbedPage,
    resolve: {
      project: projectResolver,
    },
  },
  {
    path: 'v2/:tab/projects/:slug/:project-tab/:id',
    redirectTo: '/projects/:slug/:project-tab/:id',
    pathMatch: 'prefix',
  },
  {
    path: 'v2/:tab/projects/:slug',
    redirectTo: '/projects/:slug',
    pathMatch: 'prefix',
  },
  {
    path: ':tab/projects/:slug',
    redirectTo: '/projects/:slug',
    pathMatch: 'prefix',
  },
  {
    path: 'v2',
    redirectTo: '/feed',
    pathMatch: 'prefix',
  },
  {
    path: '404',
    data: { title: 'page.not_found' },
    loadComponent: () =>
      import('./pages/not-found/not-found.page').then((m) => m.NotFoundPage),
  },
  {
    path: 'no-access',
    data: { title: 'page.no_access' },
    loadComponent: () =>
      import('./pages/no-access/no-access.page').then((m) => m.NoAccessPage),
  },
  {
    path: '**',
    redirectTo: '404',
  },
]);
